import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Navbar from "../components/Navbar";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import featureGatingService from "../services/featureGatingService";

import SelectMenu from "../components/crm/SelectMenu";
// Firestore fields users can map spreadsheet columns onto. "parties" is a
// pseudo-field: a single "X Vs Y" column that gets split into petitioner and
// respondent during parsing.
const IMPORT_FIELDS = [
  { key: "caseNumber", label: "Case Number", required: true },
  { key: "caseType", label: "Case Type", required: true },
  { key: "caseYear", label: "Case Year", required: true },
  { key: "parties", label: "Parties (“X Vs Y” combined)" },
  { key: "petitionerName", label: "Petitioner Name" },
  { key: "respondentName", label: "Respondent Name" },
  { key: "caseFiledDate", label: "Filing Date", isDate: true },
  { key: "nextHearingDate", label: "Next Hearing Date", isDate: true },
  { key: "previousHearingDate", label: "Previous Hearing Date", isDate: true },
  { key: "courtName", label: "Court Name" },
  { key: "authorityDepartment", label: "Department" },
  { key: "village", label: "Village / Division" },
  { key: "properties", label: "Khasra / Plot Nos" },
  { key: "area", label: "Area" },
  { key: "stayOrderDate", label: "Stay / Interim Order Date", isDate: true },
  { key: "counterAffidavitStatus", label: "Counter Affidavit Status" },
  { key: "prayers", label: "Prayers" },
  { key: "caseNotes", label: "Remarks / Notes" },
  { key: "petitionerAdvocate", label: "Petitioner Advocate" },
  { key: "respondentAdvocate", label: "Respondent Advocate" },
  { key: "partyContactNumber", label: "Party Contact No" },
  { key: "status", label: "Case Status" },
];

// Normalized header text -> field key. Matched after lowercasing and
// stripping punctuation, by equality first and containment second.
const HEADER_SYNONYMS = {
  caseNumber: ["case no", "case number", "wp no", "w p no", "petition no", "case no."],
  caseType: ["nature of petition", "case type", "petition type"],
  caseYear: ["year", "case year"],
  parties: ["name and party name", "party name", "case title", "parties", "title"],
  petitionerName: ["petitioner name", "petitioner"],
  respondentName: ["respondent name", "respondent"],
  caseFiledDate: ["date of filing", "filing date", "filed date", "case filed date"],
  nextHearingDate: ["next hearing date", "next hearing", "next date", "ndoh"],
  previousHearingDate: ["previous hearing date", "previous hearing", "last hearing"],
  courtName: ["court name", "court"],
  authorityDepartment: ["deptt", "department", "dept"],
  village: ["village division", "village", "division"],
  properties: ["khasra nos plot nos", "khasra", "plot nos", "plot no", "properties", "property"],
  area: ["area"],
  stayOrderDate: ["intrem order stay status quo", "interim order stay status quo", "stay order", "interim order", "stay"],
  counterAffidavitStatus: ["ca filed y or n", "c a filed y or n", "ca filed", "counter affidavit", "ca status"],
  prayers: ["prayers", "prayer"],
  caseNotes: ["remarks", "case notes", "notes"],
  petitionerAdvocate: ["petitioners advocate", "petitioner advocate", "petitioner s advocate"],
  respondentAdvocate: ["gnida advocate", "respondent advocate", "respondent s advocate", "authority advocate"],
  partyContactNumber: ["party contact no", "contact no", "mobile", "phone"],
  status: ["status", "case status"],
};

const VALID_STATUSES = [
  "NA",
  "pending",
  "disposed",
  "pre Admission",
  "dismissed",
  "admitted",
  "adjourn",
  "rejected",
];

const PENDING_STATUSES = ["pending", "NA", "pre Admission", "admitted", "adjourn"];

const normalizeHeader = (h) =>
  String(h || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const autoMapHeaders = (headers) => {
  const mapping = {}; // field key -> column index
  const usedCols = new Set();
  const normalized = headers.map(normalizeHeader);
  // Two passes: exact synonym match wins over containment match.
  [true, false].forEach((exact) => {
    IMPORT_FIELDS.forEach(({ key }) => {
      if (mapping[key] !== undefined) return;
      const synonyms = HEADER_SYNONYMS[key] || [];
      const idx = normalized.findIndex(
        (h, i) =>
          h &&
          !usedCols.has(i) &&
          synonyms.some((s) => (exact ? h === s : h.includes(s)))
      );
      if (idx !== -1) {
        mapping[key] = idx;
        usedCols.add(idx);
      }
    });
  });
  return mapping;
};

// Accepts JS Date (from cellDates), Excel serial numbers, and the
// dd.mm.yyyy / dd/mm/yyyy / dd-mm-yyyy strings common in Indian registers.
const parseDateValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date && !isNaN(value)) return value;
  if (typeof value === "number" && value > 25569 && value < 80000) {
    const d = new Date(Math.round((value - 25569) * 86400 * 1000));
    return isNaN(d) ? null : d;
  }
  const match = String(value).match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (match) {
    const d = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    return isNaN(d) ? null : d;
  }
  return null;
};

const formatDate = (date) => {
  if (!date) return null;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

// "Sewak Shiksha Samiti Vs State of U.P." -> { petitioner, respondent }
const splitParties = (value) => {
  const text = String(value || "").trim();
  if (!text) return { petitioner: "", respondent: "" };
  const parts = text.split(/\s+(?:vs\.?|v\/s|versus)\s*,?\s+/i);
  if (parts.length >= 2) {
    return {
      petitioner: parts[0].trim().replace(/[,]+$/, ""),
      respondent: parts.slice(1).join(" Vs ").trim().replace(/[,]+$/, ""),
    };
  }
  return { petitioner: text, respondent: "" };
};

const mapCounterAffidavit = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (lower === "n" || lower === "no" || lower === "not initiated") return "Not Initiated";
  if (lower === "y" || lower === "yes" || lower === "filed") return "Filed (Physical/e-Filing)";
  return text; // keep register wording like "Parawise sent dt. 19.07.2019"
};

const normalizeStatus = (value, fallback) => {
  const text = String(value || "").trim().toLowerCase();
  const hit = VALID_STATUSES.find((s) => s.toLowerCase() === text);
  return hit || fallback;
};

const ImportCases = () => {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [rows, setRows] = useState([]); // parsed case rows
  const [defaultStatus, setDefaultStatus] = useState("pending");
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  const buildRows = (rawRows, headers, mapping, statusFallback, existingCaseNos) => {
    const getCell = (raw, key) =>
      mapping[key] !== undefined ? raw[mapping[key]] : undefined;
    const seenInFile = new Set();

    return rawRows.map((raw, i) => {
      let petitionerName = String(getCell(raw, "petitionerName") || "").trim();
      let respondentName = String(getCell(raw, "respondentName") || "").trim();
      if (!petitionerName && mapping.parties !== undefined) {
        const split = splitParties(getCell(raw, "parties"));
        petitionerName = split.petitioner;
        respondentName = respondentName || split.respondent;
      }

      const stayRaw = getCell(raw, "stayOrderDate");
      const statusCell = String(getCell(raw, "status") || "").trim();
      const row = {
        id: i,
        include: true,
        statusFromFile: VALID_STATUSES.some(
          (s) => s.toLowerCase() === statusCell.toLowerCase()
        ),
        caseNumber: String(getCell(raw, "caseNumber") ?? "").trim(),
        caseType: String(getCell(raw, "caseType") || "").trim(),
        caseYear: String(getCell(raw, "caseYear") ?? "").trim(),
        petitionerName,
        respondentName,
        caseFiledDate: parseDateValue(getCell(raw, "caseFiledDate")),
        nextHearingDate: parseDateValue(getCell(raw, "nextHearingDate")),
        previousHearingDate: parseDateValue(getCell(raw, "previousHearingDate")),
        courtName: String(getCell(raw, "courtName") || "").trim(),
        authorityDepartment: String(getCell(raw, "authorityDepartment") || "").trim(),
        village: String(getCell(raw, "village") || "").trim(),
        properties: String(getCell(raw, "properties") || "").trim(),
        area: String(getCell(raw, "area") || "").trim(),
        stayOrderDate: parseDateValue(stayRaw),
        counterAffidavitStatus: mapCounterAffidavit(getCell(raw, "counterAffidavitStatus")),
        prayers: String(getCell(raw, "prayers") || "").trim(),
        caseNotes: String(getCell(raw, "caseNotes") || "").trim(),
        petitionerAdvocate: String(getCell(raw, "petitionerAdvocate") || "").trim(),
        respondentAdvocate: String(getCell(raw, "respondentAdvocate") || "").trim(),
        partyContactNumber: String(getCell(raw, "partyContactNumber") || "").trim(),
        status: normalizeStatus(getCell(raw, "status"), statusFallback),
      };

      const problems = [];
      if (!row.caseNumber) problems.push("Missing case number");
      if (!row.caseType) problems.push("Missing case type");
      if (!row.caseYear) problems.push("Missing year");
      if (!row.petitionerName) problems.push("Missing petitioner");
      if (row.caseNumber && existingCaseNos.has(row.caseNumber)) {
        problems.push("Already exists in your cases");
      }
      if (row.caseNumber && seenInFile.has(row.caseNumber)) {
        problems.push("Duplicate within file");
      }
      if (row.caseNumber) seenInFile.add(row.caseNumber);

      row.problems = problems;
      row.include = problems.length === 0;
      return row;
    });
  };

  const handleFile = async (file) => {
    if (!file || !globalUser?.uid) return;
    setIsParsing(true);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { cellDates: true });
      // Use the first sheet that actually has rows (registers often carry
      // empty extra sheets).
      let sheetRows = [];
      for (const name of workbook.SheetNames) {
        sheetRows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
          header: 1,
          defval: "",
        });
        if (sheetRows.length > 1) break;
      }
      if (sheetRows.length < 2) {
        toast.error("No data rows found in the file");
        return;
      }

      const headers = sheetRows[0].map((h) => String(h ?? "").trim());
      const mapping = autoMapHeaders(headers);
      if (mapping.caseNumber === undefined && mapping.parties === undefined) {
        toast.error(
          "Could not recognise the columns. Please make sure the first row has headers like Case No, Year, Party Name."
        );
        return;
      }

      // One-time fetch of the user's existing case numbers for duplicate flags.
      const [pendingSnap, disposedSnap] = await Promise.all([
        getDocs(query(collection(db, "pending"), where("owner", "==", globalUser.uid))),
        getDocs(query(collection(db, "disposed"), where("owner", "==", globalUser.uid))),
      ]);
      const existingCaseNos = new Set();
      pendingSnap.forEach((d) => d.data().caseNo && existingCaseNos.add(String(d.data().caseNo).trim()));
      disposedSnap.forEach((d) => d.data().caseNo && existingCaseNos.add(String(d.data().caseNo).trim()));

      const dataRows = sheetRows
        .slice(1)
        .filter((r) => r.some((cell) => String(cell ?? "").trim() !== ""));
      setRows(buildRows(dataRows, headers, mapping, defaultStatus, existingCaseNos));
      setFileName(file.name);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error("Could not read the file. Please upload a valid Excel or CSV file.");
    } finally {
      setIsParsing(false);
    }
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        // Re-validate the required fields after an inline edit.
        updated.problems = updated.problems.filter(
          (p) =>
            !(
              (field === "caseNumber" && p === "Missing case number") ||
              (field === "caseType" && p === "Missing case type") ||
              (field === "caseYear" && p === "Missing year") ||
              (field === "petitionerName" && p === "Missing petitioner")
            ) || !String(value).trim()
        );
        return updated;
      })
    );
  };

  const selectedRows = useMemo(() => rows.filter((r) => r.include), [rows]);

  const handleImport = async () => {
    if (!globalUser?.uid) {
      toast.error("User not authenticated");
      return;
    }
    if (selectedRows.length === 0) {
      toast.error("No rows selected for import");
      return;
    }
    const blocked = selectedRows.filter((r) =>
      r.problems.some((p) => p.startsWith("Missing"))
    );
    if (blocked.length > 0) {
      toast.error(`${blocked.length} selected row(s) still have missing required fields`);
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    try {
      const uid = globalUser.uid;
      const pendingRows = selectedRows.filter((r) => PENDING_STATUSES.includes(r.status));
      const disposedRows = selectedRows.filter((r) => !PENDING_STATUSES.includes(r.status));

      for (const [collectionName, group] of [
        ["pending", pendingRows],
        ["disposed", disposedRows],
      ]) {
        if (group.length === 0) continue;
        const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
          uid,
          group.length,
          collectionName
        );
        if (!limitResult.canProceed) {
          toast.error(
            `Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${collectionName} cases. Please upgrade or import fewer cases.`
          );
          setIsImporting(false);
          return;
        }
      }

      const userCollectionName =
        localStorage.getItem("userType") === "client" ? "client" : "lawyers";
      const userDoc = await getDoc(doc(db, userCollectionName, uid));
      const sharingGroups =
        userDoc.exists() && Array.isArray(userDoc.data().sharingGroups)
          ? userDoc.data().sharingGroups
          : [];

      let pendingCount = 0;
      if (pendingRows.length > 0) {
        const pendingSnap = await getDocs(
          query(collection(db, "pending"), where("owner", "==", uid))
        );
        pendingCount = pendingSnap.size;
      }

      let written = 0;
      const total = selectedRows.length;
      const CHUNK = 200; // well under the 500-op writeBatch limit

      for (const [collectionName, group] of [
        ["pending", pendingRows],
        ["disposed", disposedRows],
      ]) {
        for (let start = 0; start < group.length; start += CHUNK) {
          const chunk = group.slice(start, start + CHUNK);
          const batch = writeBatch(db);
          for (const row of chunk) {
            let subscriptionMetadata = null;
            if (collectionName === "pending") {
              subscriptionMetadata =
                await featureGatingService.getSubscriptionMetadataForNewCase(
                  uid,
                  pendingCount
                );
              pendingCount += 1;
            }
            const caseRef = doc(collection(db, collectionName));
            batch.set(caseRef, {
              respondentName: row.respondentName,
              appearingFor: "",
              caseNo: row.caseNumber,
              caseType: row.caseType,
              caseYear: row.caseYear,
              casenotes: row.caseNotes,
              courtName: row.courtName,
              files: [],
              caseFiledDate: formatDate(row.caseFiledDate),
              nextHearingDate: formatDate(row.nextHearingDate),
              previousHearingDate: formatDate(row.previousHearingDate),
              owner: uid,
              partyContactNo: row.partyContactNumber,
              petitionerName: row.petitionerName,
              petitionerRelation: "",
              petitionerRelationName: "",
              respondentRelation: "",
              respondentRelationName: "",
              status: row.status,
              team: [uid],
              sharedGroupId: sharingGroups[0] || null,
              allPetitioners: [],
              allRespondents: [],
              petitionerAdvocate: row.petitionerAdvocate,
              respondentAdvocate: row.respondentAdvocate,
              allPetitionerAdvocates: [],
              allRespondentAdvocates: [],
              authorityDepartment: row.authorityDepartment,
              authorityDepartmentOther: "",
              properties: row.properties,
              area: row.area,
              village: row.village,
              stayOrderDate: formatDate(row.stayOrderDate),
              counterAffidavitStatus: row.counterAffidavitStatus,
              counterAppealFiled: "",
              prayers: row.prayers,
              importedFrom: fileName,
              ...(subscriptionMetadata && { subscriptionMetadata }),
              timestamp: serverTimestamp(),
            });
          }
          await batch.commit();
          written += chunk.length;
          setImportProgress(Math.round((written / total) * 100));
        }
      }

      setImportResult({
        imported: written,
        pending: pendingRows.length,
        disposed: disposedRows.length,
        skipped: rows.length - selectedRows.length,
      });
      toast.success(`${written} cases imported successfully`);
    } catch (error) {
      console.error("Error importing cases:", error);
      toast.error("Import failed. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent text-white text-sm p-1 border-b border-transparent focus:border-yellow-500 focus:outline-none";

  return (
    <div className="min-h-screen bg-[#0c0a29] text-white relative">
      <Navbar />
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>

      <main className="px-4 md:px-16 py-8 z-10 relative">
        <PageHeader
          title="Import Cases"
          subtitle="Bulk-import cases from an Excel sheet"
          backTo="/portal/AddCase"
        />

        {importResult ? (
          <div className="bg-[#1A1535] rounded-2xl p-8 max-w-xl mx-auto text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Import Complete</h2>
            <p className="text-gray-300 mb-1">
              {importResult.imported} cases imported ({importResult.pending} pending,{" "}
              {importResult.disposed} disposed)
            </p>
            {importResult.skipped > 0 && (
              <p className="text-gray-400 text-sm">
                {importResult.skipped} rows were skipped
              </p>
            )}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => {
                  setRows([]);
                  setFileName("");
                  setImportResult(null);
                }}
                className="px-4 py-2 border border-white rounded-lg hover:bg-white/10"
              >
                Import Another File
              </button>
              <button
                onClick={() => navigate("/portal/AllCases")}
                className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400"
              >
                View All Cases
              </button>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="max-w-2xl mx-auto">
            <div
              className="bg-[#1A1535] rounded-2xl p-10 border-2 border-dashed border-gray-600 hover:border-yellow-500 transition-colors text-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              {isParsing ? (
                <Loader2 className="w-10 h-10 text-yellow-500 mx-auto animate-spin" />
              ) : (
                <FileSpreadsheet className="w-10 h-10 text-yellow-500 mx-auto" />
              )}
              <p className="mt-4 text-lg font-medium">
                {isParsing ? "Reading file..." : "Drop your Excel / CSV file here"}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                or click to browse (.xlsx, .xls, .csv)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
            <div className="mt-6 text-sm text-gray-400 bg-[#1A1535] rounded-xl p-4">
              <p className="font-medium text-gray-300 mb-2">How it works</p>
              <ul className="list-disc list-inside space-y-1">
                <li>The first row of your sheet must contain column headings.</li>
                <li>
                  Recognised headings include: Case No / W.P. No, Nature of Petition,
                  Year, Name and Party Name, Date of Filing, Deptt., Village/Division,
                  Khasra/Plot Nos, Stay, C.A. Filed, Prayers, Remarks, Advocates.
                </li>
                <li>You can review and edit every case before anything is saved.</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="text-gray-300">
                <span className="text-white font-medium">{fileName}</span> —{" "}
                {rows.length} rows found, {selectedRows.length} selected
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-300">Default status:</label>
                <SelectMenu
                  value={defaultStatus}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDefaultStatus(next);
                    setRows((prev) =>
                      prev.map((r) => ({
                        ...r,
                        status: VALID_STATUSES.includes(r.status) && r.statusFromFile
                          ? r.status
                          : next,
                      }))
                    );
                  }}
                  className="bg-[#0A061E] text-white p-2 rounded-lg border border-gray-700"
                >
                  {VALID_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </SelectMenu>
                <button
                  onClick={() => {
                    setRows([]);
                    setFileName("");
                  }}
                  className="px-3 py-2 border border-white rounded-lg hover:bg-white/10 flex items-center gap-2 text-sm"
                >
                  <Trash2 size={16} /> Discard
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || selectedRows.length === 0}
                  className="px-5 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Importing... {importProgress}%
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Import {selectedRows.length} Cases
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-[#1A1535] rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-yellow-500 border-b border-gray-700">
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.length === rows.length}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((r) => ({ ...r, include: e.target.checked }))
                          )
                        }
                      />
                    </th>
                    <th className="p-3">Case No</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Year</th>
                    <th className="p-3 min-w-[200px]">Petitioner</th>
                    <th className="p-3 min-w-[160px]">Respondent</th>
                    <th className="p-3 whitespace-nowrap">Filed</th>
                    <th className="p-3 whitespace-nowrap">Prev. Hearing</th>
                    <th className="p-3 whitespace-nowrap">Next Hearing</th>
                    <th className="p-3 min-w-[140px]">Court</th>
                    <th className="p-3 min-w-[120px]">Department</th>
                    <th className="p-3 min-w-[150px]">Village / Division</th>
                    <th className="p-3 min-w-[150px]">Khasra / Plot</th>
                    <th className="p-3 min-w-[100px]">Area</th>
                    <th className="p-3 whitespace-nowrap">Stay</th>
                    <th className="p-3 min-w-[120px]">C.A.</th>
                    <th className="p-3 min-w-[160px]">Prayers</th>
                    <th className="p-3 min-w-[160px]">Remarks</th>
                    <th className="p-3 min-w-[140px]">Pet. Advocate</th>
                    <th className="p-3 min-w-[140px]">Resp. Advocate</th>
                    <th className="p-3 min-w-[120px]">Contact</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 min-w-[180px]">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-b border-gray-800 ${
                        row.problems.length > 0 ? "bg-red-900/10" : ""
                      }`}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={row.include}
                          onChange={(e) => updateRow(row.id, "include", e.target.checked)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.caseNumber}
                          onChange={(e) => updateRow(row.id, "caseNumber", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.caseType}
                          onChange={(e) => updateRow(row.id, "caseType", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={`${inputClass} w-16`}
                          value={row.caseYear}
                          onChange={(e) => updateRow(row.id, "caseYear", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.petitionerName}
                          onChange={(e) => updateRow(row.id, "petitionerName", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.respondentName}
                          onChange={(e) => updateRow(row.id, "respondentName", e.target.value)}
                        />
                      </td>
                      <td className="p-3 whitespace-nowrap text-gray-300">
                        {formatDate(row.caseFiledDate) || "—"}
                      </td>
                      <td className="p-3 whitespace-nowrap text-gray-300">
                        {formatDate(row.previousHearingDate) || "—"}
                      </td>
                      <td className="p-3 whitespace-nowrap text-gray-300">
                        {formatDate(row.nextHearingDate) || "—"}
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.courtName}
                          onChange={(e) => updateRow(row.id, "courtName", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.authorityDepartment}
                          onChange={(e) => updateRow(row.id, "authorityDepartment", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.village}
                          onChange={(e) => updateRow(row.id, "village", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.properties}
                          onChange={(e) => updateRow(row.id, "properties", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.area}
                          onChange={(e) => updateRow(row.id, "area", e.target.value)}
                        />
                      </td>
                      <td className="p-3 whitespace-nowrap text-gray-300">
                        {formatDate(row.stayOrderDate) || "—"}
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.counterAffidavitStatus}
                          onChange={(e) => updateRow(row.id, "counterAffidavitStatus", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.prayers}
                          onChange={(e) => updateRow(row.id, "prayers", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.caseNotes}
                          onChange={(e) => updateRow(row.id, "caseNotes", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.petitionerAdvocate}
                          onChange={(e) => updateRow(row.id, "petitionerAdvocate", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.respondentAdvocate}
                          onChange={(e) => updateRow(row.id, "respondentAdvocate", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          className={inputClass}
                          value={row.partyContactNumber}
                          onChange={(e) => updateRow(row.id, "partyContactNumber", e.target.value)}
                        />
                      </td>
                      <td className="p-3">
                        <SelectMenu
                          value={row.status}
                          onChange={(e) => updateRow(row.id, "status", e.target.value)}
                          className="bg-[#0A061E] text-white p-1 rounded border border-gray-700 text-sm"
                        >
                          {VALID_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </SelectMenu>
                      </td>
                      <td className="p-3">
                        {row.problems.length > 0 ? (
                          <span className="flex items-center gap-1 text-red-400 text-xs">
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            {row.problems.join("; ")}
                          </span>
                        ) : (
                          <span className="text-green-400 text-xs">Ready</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gray-400 text-xs mt-3">
              Scroll the table sideways to see every imported column — department,
              village/division, khasra/plot, area, stay date, counter-affidavit status,
              prayers, remarks, advocates and contact. You can edit any field before saving.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default ImportCases;
